/**
 * TextareaMarkdownIndentUtil - I re-indent the lists in a markdown string so
 * their nesting survives the import. ONLY concerned with the text - no editor,
 * no rendering.
 */

/** A list item at any indent: `-`, `*`, `+`, `1.` or `1)`. */
const LIST_ITEM = /^(\s*)([*+-]|\d+[).])(\s+)(.*)$/;

/** A fenced block's opening or closing line, capturing the run itself so the two can be matched. */
const FENCE = /^\s*(`{3,}|~{3,})/;

/**
 * **`TextareaMarkdownUtil`'s importer counts four spaces to a level, and
 * markdown is commonly written with two.** Two spaces is ordinary CommonMark,
 * so a nested list renders as a nested list everywhere except here, where every
 * item arrives at the same depth in one flat list - measured: two and three
 * spaces give one list and no nesting, four spaces and a tab give two.
 *
 * Depth is taken from the *structure* rather than the character count, so text
 * that already indents by four - or by a tab, or inconsistently - is read the
 * same way and comes out unchanged.
 */
export const TextareaMarkdownIndentUtil = {
  /** Tabs count as one level, which is how the importer reads them. */
  indentWidthOf({ indent }: { indent: string }) {
    return indent.replaceAll('\t', '    ').length;
  },

  reindentLists({ text }: { text: string }) {
    /*
      Indent widths of the levels currently open, so depth is
      `openLevels.length - 1`. A stack rather than arithmetic on the original
      width: that is what lets two-space, four-space and tabbed text all resolve
      to the same depths.
    */
    const openLevels: number[] = [];
    /** The run that opened the block, held so only its own kind can close it. */
    let fence: string | null = null;

    return text
      .split('\n')
      .map(line => {
        // Inside a fence every line is content - an indented line there is code, not a list.
        const run = FENCE.exec(line)?.[1];

        if (run) {
          /*
            **Only the same character, and at least as long.** `~~~` inside a backtick block is
            content, so closing on any fence would leave the flag inverted from the real close
            onwards - silently reindenting the code and nothing after it.
          */
          if (fence === null) fence = run;
          else if (run.startsWith(fence)) fence = null;

          return line;
        }

        if (fence !== null) return line;

        const item = LIST_ITEM.exec(line);

        if (!item) {
          /*
            A blank line sits inside a list often enough to leave the levels
            open; anything else at the left margin has ended the list, and an
            indented line is a continuation of the item above, which keeps its
            own indentation.
          */
          if (line.trim() === '') return line;
          if (!/^\s/.test(line)) openLevels.length = 0;

          return line;
        }

        const [, indent, marker, gap, content] = item;
        const width = TextareaMarkdownIndentUtil.indentWidthOf({
          indent: indent ?? '',
        });

        while (openLevels.length > 0 && width < (openLevels.at(-1) ?? 0)) {
          openLevels.pop();
        }

        if (openLevels.length === 0 || width > (openLevels.at(-1) ?? 0)) {
          openLevels.push(width);
        }

        return `${' '.repeat((openLevels.length - 1) * 4)}${marker}${gap}${content}`;
      })
      .join('\n');
  },
};
